import dayjs from "dayjs";
import { collection, onSnapshot } from "firebase/firestore";
import {
    AlertCircle,
    CheckCircle,
    Clock,
    Phone,
    Search,
    X,
    ChevronUp,
    ChevronDown,
    Wifi,
    Server,
    ArrowUpDown
} from 'lucide-react';
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import CustomSelect from "../components/common/CustomSelect";
import { Loading } from "../components/common/Loading";
import ComplaintForm from "../components/ComplaintForm";
import Navbar from "../components/header/Navbar";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import { CACHE_KEYS, getCachedData } from "../services/localStorageService";

const statusOptions = ["pending", "in-operation", "paused", "completed"];
const statusColors = {
    'pending': 'bg-yellow-100 text-yellow-800',
    'in-operation': 'bg-blue-100 text-blue-800',
    'paused': 'bg-gray-100 text-gray-800',
    'completed': 'bg-green-100 text-green-800'
};
const statusIcons = {
    'pending': <Clock className="w-3.5 h-3.5" />,
    'in-operation': <AlertCircle className="w-3.5 h-3.5" />,
    'paused': <Clock className="w-3.5 h-3.5" />,
    'completed': <CheckCircle className="w-3.5 h-3.5" />
};
// const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);
const resolveTimeOptions = [
    // Even hours from 2 to 24
    ...Array.from({ length: 12 }, (_, i) => `${(i + 1) * 2} hours`),
    // Days
    "1 day",
    "2 days",
    "3 days",
    // Weeks
    "1 week"
];

const AllComplaints = () => {
    const { user, loading: isLoading, users } = useAuth();
    const [complaints, setComplaints] = useState([]);
    const [filteredComplaints, setFilteredComplaints] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        category: "",
        assignedTo: ""
    });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Sorting state
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    // Helper function to get value from onu/router (handle arrays)
    const getStatusValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value[0] : '-';
        }
        return value || '-';
    };

    // Handle phone call
    const handlePhoneClick = (phoneNumber) => {
        if (!phoneNumber) return;
        if (window.confirm(`Do you want to call ${phoneNumber}?`)) {
            window.location.href = `tel:${phoneNumber}`;
        }
    };

    // Load complaints with real-time updates
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "complaints"),
            (snapshot) => {
                const allComplaints = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                let filtered = allComplaints;
                if (user?.role === "technician") {
                    filtered = filtered.filter(
                        (c) => c.assignedTo === user.uid
                    );
                } else if (user?.role === "helpdesk") {
                    filtered = filtered.filter(
                        (c) => c.createdBy === user.uid
                    );
                }

                filtered.sort(
                    (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
                );

                setComplaints(filtered);
                setFilteredComplaints(filtered);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load complaints");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Load categories from cache
    useEffect(() => {
        const loadData = () => {
            try {
                const categories = getCachedData(CACHE_KEYS.CATEGORIES);
                if (categories) {
                    setCategories(categories);
                }
                setLoading(false);
            } catch (error) {
                console.error("Error loading data from cache:", error);
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Apply filters and sorting
    useEffect(() => {
        let filtered = [...complaints];

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                c.ticketNumber?.toLowerCase().includes(term) ||
                c.customerInfo?.name?.toLowerCase().includes(term) ||
                c.customerInfo?.phone?.includes(term) ||
                c.category?.toLowerCase().includes(term)
            );
        }

        // Status filter
        if (filters.status) {
            filtered = filtered.filter(c => c.status === filters.status);
        }

        // Category filter
        if (filters.category) {
            filtered = filtered.filter(c => c.category === filters.category);
        }

        // Assigned filter
        if (filters.assignedTo) {
            filtered = filtered.filter(c => c.assignedTo === filters.assignedTo);
        }

        // Apply sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle nested objects
                if (sortConfig.key === 'customerName') {
                    aVal = a.customerInfo?.name || '';
                    bVal = b.customerInfo?.name || '';
                } else if (sortConfig.key === 'category') {
                    aVal = a.category || '';
                    bVal = b.category || '';
                }

                // Handle null/undefined values
                if (aVal === undefined || aVal === null) aVal = '';
                if (bVal === undefined || bVal === null) bVal = '';

                // Convert to string for comparison if needed
                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        setFilteredComplaints(filtered);
        setCurrentPage(1);
    }, [complaints, searchTerm, filters, sortConfig]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Get status badge
    const StatusBadge = ({ status }) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
            {statusIcons[status]}
            {status}
        </span>
    );

    // Render sort indicator
    const SortIndicator = ({ column }) => {
        if (sortConfig.key !== column) {
            return <ArrowUpDown className="w-3 h-3 text-gray-400 inline ml-1" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp className="w-3 h-3 text-pink-500 inline ml-1" />
            : <ChevronDown className="w-3 h-3 text-pink-500 inline ml-1" />;
    };

    // Render ONU/Router status beautifully
    const renderNetworkStatus = (complaint) => {
        if (complaint.complaintType !== "Red signal") return null;

        return (
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 text-[10px]">
                {/* ONU Status */}
                <div className="bg-blue-50/70 rounded p-1.5 border border-blue-100/50">
                    <div className="flex items-center gap-1 text-blue-700 font-medium text-[9px]">
                        <Server className="w-3 h-3" />
                        <span>ONU</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-[8px]">PWR</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.power)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">PON</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.pon) === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.pon)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">LAN</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.lan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.lan)}
                        </span>
                    </div>
                </div>

                {/* Router Status */}
                <div className="bg-purple-50/70 rounded p-1.5 border border-purple-100/50">
                    <div className="flex items-center gap-1 text-purple-700 font-medium text-[9px]">
                        <Wifi className="w-3 h-3" />
                        <span>Router</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-[8px]">PWR</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.power)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">WLAN</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.wanLan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.wanLan)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">WiFi</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.wifi) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.wifi)?.slice(0, 8)}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // Pagination
    const totalPages = Math.ceil(filteredComplaints.length / itemsPerPage);
    const paginatedComplaints = filteredComplaints.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const clearFilters = () => {
        setSearchTerm("");
        setFilters({
            status: "",
            category: "",
            assignedTo: ""
        });
        setSortConfig({ key: null, direction: 'asc' });
    };

    if (loading || isLoading) return <Loading />;

    return (
        <>
            <Navbar user={user} />

            {/* Modal for Complaint Form */}
            {isFormVisible && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setIsFormVisible(false);
                        }
                    }}
                >
                    <div className="w-full max-w-4xl max-h-[90vh] my-8">
                        <ComplaintForm
                            onClose={() => setIsFormVisible(false)}
                        />
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 space-y-4">
                {/* Header with Search and Filters */}
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="w-full sm:w-auto flex items-center gap-3">
                            <h1 className="text-xl font-bold text-gray-800">All Complaints</h1>
                            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                {filteredComplaints.length}
                            </span>
                        </div>
                        <button
                            onClick={() => setIsFormVisible(true)}
                            className="px-3 py-1.5 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 transition shadow-md hover:shadow-pink-500/25 whitespace-nowrap w-full sm:w-auto"
                        >
                            + New Complaint
                        </button>
                    </div>

                    {/* Search and Filters Row */}
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-[180px]">
                            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by ticket, customer, phone, category..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                            <CustomSelect
                                name="status"
                                placeholder="Status"
                                options={statusOptions}
                                value={filters.status}
                                onChange={(name, value) => setFilters(prev => ({ ...prev, status: value }))}
                                className="w-[100px] text-xs"
                            />
                            {/* Mobile category filter - shows as dropdown on mobile */}
                            <div className="block lg:hidden">
                                <CustomSelect
                                    name="category"
                                    placeholder="Category"
                                    options={categories.map(c => c.name)}
                                    value={filters.category}
                                    onChange={(name, value) => setFilters(prev => ({ ...prev, category: value }))}
                                    className="w-[110px] text-xs"
                                />
                            </div>
                            <CustomSelect
                                name="assignedTo"
                                placeholder="Assigned"
                                options={users.map(u => u.name)}
                                value={filters.assignedTo}
                                onChange={(name, value) => {
                                    const user = users.find(u => u.name === value);
                                    setFilters(prev => ({ ...prev, assignedTo: user?.uid || "" }));
                                }}
                                className="w-[110px] text-xs"
                            />
                            {(searchTerm || Object.values(filters).some(f => f) || sortConfig.key) && (
                                <button
                                    onClick={clearFilters}
                                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition flex items-center gap-0.5"
                                    title="Clear filters"
                                >
                                    <X className="w-3.5 h-3.5" />
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Category chips - desktop only */}
                    <div className="hidden lg:flex flex-wrap gap-1.5 mt-1">
                        {categories.map(c => (
                            <span
                                key={c.id}
                                className={`cursor-pointer inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-[10px] font-medium transition-all hover:scale-105 ${filters.category === c.name
                                    ? "bg-pink-100 text-pink-700 border border-pink-300"
                                    : "bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200"
                                    }`}
                                onClick={() => setFilters(prev => ({ ...prev, category: prev.category === c.name ? "" : c.name }))}
                            >
                                {c.icon || '📌'} {c.name}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th
                                        className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition w-[70px]"
                                        onClick={() => handleSort('createdAt')}
                                    >
                                        Date <SortIndicator column="createdAt" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[90px]"
                                    >
                                        Ticket
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[100px]"
                                    >
                                        Customer
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition w-[100px]"
                                        onClick={() => handleSort('category')}
                                    >
                                        Category
                                        <SortIndicator column="category" />
                                    </th>

                                    <th
                                        className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[120px]"
                                    >
                                        Assigned To
                                    </th>
                                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex-1 flex-grow">
                                        Description
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition w-[110px]"
                                        onClick={() => handleSort('status')}
                                    >
                                        Status <SortIndicator column="status" />
                                    </th>

                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {paginatedComplaints.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                            <div className="flex flex-col items-center gap-1">
                                                <span className="text-4xl">📭</span>
                                                <p className="font-medium">No complaints found</p>
                                                <p className="text-xs text-gray-400">
                                                    {searchTerm || Object.values(filters).some(f => f)
                                                        ? 'Try adjusting your filters'
                                                        : 'Create your first complaint'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedComplaints.map((c) => (
                                        <tr key={c.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                                            <td className="px-2 py-2 whitespace-nowrap text-gray-600 text-xs">
                                                {dayjs(c.createdAt?.toDate()).format("DD-MM-YY")}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap font-mono text-xs font-semibold text-gray-700">
                                                {c.ticketNumber}
                                            </td>
                                            <td className="px-2 py-2 truncate max-w-[120px]" title={c.customerInfo?.name}>
                                                <div>
                                                    <div className="flex items-center gap-1">
                                                        <p className="text-sm font-medium text-gray-800 truncate">{c.customerInfo?.name}</p>
                                                        {c.customerInfo?.phone && (
                                                            <button
                                                                onClick={() => handlePhoneClick(c.customerInfo.phone)}
                                                                className="p-0.5 text-blue-500 hover:text-blue-700 transition"
                                                                title={`Call ${c.customerInfo.phone}`}
                                                            >
                                                                <Phone className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-[11px] text-gray-700">{c.customerInfo?.phone}</p>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap">
                                                <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                                                    {c?.category || "-"}
                                                </span>
                                            </td>

                                            <td className="px-2 py-2 truncate max-w-[100px]" title={users.find(u => u.uid === c.assignedTo)?.name}>
                                                {c.assignedTo ? (
                                                    <span className="text-xs text-gray-600 truncate">
                                                        {users.find(u => u.uid === c.assignedTo)?.name || "Unknown"}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Unassigned</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-2">
                                                {c?.description && (
                                                    <p className="text-xs text-gray-600 line-clamp-2">{c.description}</p>
                                                )}
                                                {renderNetworkStatus(c)}
                                            </td>
                                            <td className="px-2 py-2">
                                                <StatusBadge status={c.status} />
                                            </td>


                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Desktop Pagination */}
                    {totalPages > 1 && (
                        <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                                Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredComplaints.length)} of {filteredComplaints.length}
                            </p>
                            <div className="flex gap-1.5">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <span className="px-2.5 py-1 text-xs font-medium text-gray-700">
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-3">
                    {paginatedComplaints.length === 0 ? (
                        <div className="bg-white rounded-lg shadow p-6 text-center border border-gray-100">
                            <span className="text-4xl">📭</span>
                            <p className="font-medium text-gray-800 mt-2">No complaints found</p>
                            <p className="text-sm text-gray-400">
                                {searchTerm || Object.values(filters).some(f => f)
                                    ? 'Try adjusting your filters'
                                    : 'Create your first complaint'}
                            </p>
                        </div>
                    ) : (
                        paginatedComplaints.map((c) => (
                            <div key={c.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all">
                                {/* Card Header - Always Visible */}
                                <div className="p-3 bg-white border-b border-gray-100">
                                    <div className="flex items-start justify-between">
                                        {/* Left side */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-mono text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                                                    {c.ticketNumber}
                                                </span>
                                                <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                                                    {c?.category || '-'}
                                                </span>
                                            </div>

                                            {/* Customer Info */}
                                            <div className="mt-1.5">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-sm font-medium text-gray-800 truncate">
                                                        {c.customerInfo?.name || 'N/A'}
                                                    </span>
                                                    {c.customerInfo?.phone && (
                                                        <button
                                                            onClick={() => handlePhoneClick(c.customerInfo.phone)}
                                                            className="p-0.5 text-blue-500 hover:text-blue-700 transition flex-shrink-0"
                                                            title={`Call ${c.customerInfo.phone}`}
                                                        >
                                                            <Phone className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-gray-700">
                                                    {c.customerInfo?.phone || 'No phone'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right side - Status & Date */}
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                                            <StatusBadge status={c.status} />
                                            <span className="text-[10px] text-gray-400">
                                                {dayjs(c.createdAt?.toDate()).format("DD-MM-YY")}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Card Body - Description & Network Status */}
                                <div className="p-3 space-y-2 bg-white">
                                    {/* Description */}
                                    {c?.description && (
                                        <div>
                                            <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                                Description
                                            </label>
                                            <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                {c.description}
                                            </p>
                                        </div>
                                    )}

                                    {/* Network Status (Red Signal only) */}
                                    {c.complaintType === "Red signal" && (
                                        <div className="space-y-2">
                                            <div className="bg-blue-50/70 p-2.5 rounded-lg border border-blue-200">
                                                <div className="font-medium text-xs text-blue-800 mb-1.5 flex items-center gap-1">
                                                    <Server className="w-3.5 h-3.5" />
                                                    ONU Status
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <span className="text-gray-500 block text-[8px] uppercase tracking-wider">PWR</span>
                                                        <span className={`font-bold text-sm ${getStatusValue(c.onu?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {getStatusValue(c.onu?.power)}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <span className="text-gray-500 block text-[8px] uppercase tracking-wider">PON</span>
                                                        <span className={`font-bold text-sm ${getStatusValue(c.onu?.pon) === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {getStatusValue(c.onu?.pon)}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <span className="text-gray-500 block text-[8px] uppercase tracking-wider">LAN</span>
                                                        <span className={`font-bold text-sm ${getStatusValue(c.onu?.lan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {getStatusValue(c.onu?.lan)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-purple-50/70 p-2.5 rounded-lg border border-purple-200">
                                                <div className="font-medium text-xs text-purple-800 mb-1.5 flex items-center gap-1">
                                                    <Wifi className="w-3.5 h-3.5" />
                                                    Router Status
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <span className="text-gray-500 block text-[8px] uppercase tracking-wider">PWR</span>
                                                        <span className={`font-bold text-sm ${getStatusValue(c.router?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {getStatusValue(c.router?.power)}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <span className="text-gray-500 block text-[8px] uppercase tracking-wider">WLAN</span>
                                                        <span className={`font-bold text-sm ${getStatusValue(c.router?.wanLan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {getStatusValue(c.router?.wanLan)}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white rounded p-2 text-center">
                                                        <span className="text-gray-500 block text-[8px] uppercase tracking-wider">WiFi</span>
                                                        <span className={`font-bold text-sm ${getStatusValue(c.router?.wifi) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {getStatusValue(c.router?.wifi)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Assigned To */}
                                    <div className="pt-1">
                                        <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                                            Assigned To
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <div className={`w-1.5 h-1.5 rounded-full ${c.assignedTo ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                            <span className="text-sm text-gray-700">
                                                {c.assignedTo ? (
                                                    users.find(u => u.uid === c.assignedTo)?.name || "Unknown"
                                                ) : (
                                                    <span className="text-gray-400">Unassigned</span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Mobile Pagination */}
                {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-0 px-3 py-2 border border-gray-200 rounded-lg bg-white">
                        <p className="text-xs text-gray-500 sm:flex-1 text-center sm:text-left order-2 sm:order-1">
                            Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredComplaints.length)} of {filteredComplaints.length}
                        </p>
                        <div className="flex gap-1.5 order-1 sm:order-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                ← Prev
                            </button>
                            <span className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AllComplaints;